// 🔧 volunteer.js
// ✅ Purpose: Admin page for managing all volunteer activities and time slots
// ✅ Features:
// - Manage volunteer activities and time slots
// - Assign users by email or add new pending volunteers
// - Editable activity title and description
// - Toggle to reveal/hide "Add Volunteer" form
// - Live count of filled/unfilled slots
// - XLSX export
// - Uses extracted components for cleaner code
// - Logs added throughout for data flow debugging

import React, { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db, auth, functions } from '../../firebase';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { showStatus } from '../../utils/status_helpers';
import VolunteerAddForm from '../../components/volunteer_add_form';
import AssignedVolunteersList from '../../components/assigned_volunteers_list';
import { useEventContext } from '../../hooks/use_event_context';
import { formatDateTime, toDateTime } from "../../utils/date_format";
import { httpsCallable } from 'firebase/functions';
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from '../../utils/logger';
import PromotionSourceButton from "../../promotion/components/promotion_source_button";

const Volunteers = () => {

  const {
    eventId,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });
  const [activities, setActivities] = useState([]);
  const [slotForms, setSlotForms] = useState({});
  const [status, setStatus] = useState(null);
  const [volunteerFormVisible, setVolunteerFormVisible] = useState({}); // 👈 form toggle per slot
  const [showAddActivityForm, setShowAddActivityForm] = useState(false);
  const [newActivity, setNewActivity] = useState({ title: "", description: "" });
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [expandedAll, setExpandedAll] = useState(true);

  // 🎯 Realtime activity + slot updates
  useEffect(() => {
    if (!eventId) return;
    const unsub = onSnapshot(collection(db, "events", eventId, "volunteers"), async (snap) => {
      const acts = await Promise.all(snap.docs.map(async (docSnap) => {
        const activity = { id: docSnap.id, ...docSnap.data() };
        const slotsSnap = await getDocs(collection(db, "events", eventId, "volunteers", docSnap.id, "timeSlots"));

        activity.timeSlots = slotsSnap.docs
          .map((s) => {
            const slotData = s.data() || {};

            return {
              id: s.id,
              ...slotData,
              startTime: toDateTime(slotData.startTime || slotData.startDateTime),
              endTime: toDateTime(slotData.endTime || slotData.endDateTime),

              // ✅ Safety defaults so tile UI/components never crash
              assignedUsers: Array.isArray(slotData.assignedUsers)
                ? slotData.assignedUsers
                : [],

              assignedUserIds: Array.isArray(slotData.assignedUserIds)
                ? slotData.assignedUserIds
                : [],

              volunteersNeeded: Number(slotData.volunteersNeeded || 0),
            };
          })
          .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));        

        return activity;
      }));
      acts.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
      setActivities(acts);
    });
    return () => unsub();
  }, [eventId]);

  const sendVolunteerInvite = httpsCallable(functions, "sendVolunteerInvite");
  const deleteVolunteerTimeSlot = httpsCallable(functions, "deleteVolunteerTimeSlot");
  const sendConfirmationEmail = async (email, activityId, slotId) => {
    await sendVolunteerInvite({
      email,
      eventId,
      activityId,
      slotId,
    });

    logger.log("📧 Volunteer invite email sent to", {
      value: email
    });
  };
  
  const handleSaveEdit = async (activityId) => {
    try {
      const docRef = doc(db, `events/${eventId}/volunteers/${activityId}`);

      logger.log("📌 Saving activity...", {});
      logger.log("🧾 eventId:", {
        value: eventId
      });
      logger.log("🆔 activityId:", {
        value: activityId
      });
      logger.log("📄 Firestore path:", {
        value: `events/${eventId}/volunteers/${activityId}`
      });
      logger.log("🧠 Auth UID (should be set):", {
        value: auth?.currentUser?.uid
      });
      logger.log("📦 Data to be saved:", {
        value: docRef
      });
           
      await updateDoc(docRef, {
        title: editTitle.trim(),
        description: editDescription.trim(),
      });
  
      setStatus({
        type: "success",
        message: `✅ Activity updated successfully.`,
      });
  
      setEditingActivityId(null);
      setEditTitle("");
      setEditDescription("");
    } catch (err) {
      logger.error("❌ Failed to update activity:", {
        value: err
      });
      setStatus({
        type: "error",
        message: "❌ Failed to update activity. Check console for details.",
      });
    }
  };
  
  const reloadActivities = async () => {
    if (!eventId) return;
    const snap = await getDocs(collection(db, "events", eventId, "volunteers"));
    const acts = await Promise.all(snap.docs.map(async (docSnap) => {
      const activity = { id: docSnap.id, ...docSnap.data() };
      const slotsSnap = await getDocs(collection(db, "events", eventId, "volunteers", docSnap.id, "timeSlots"));
      activity.timeSlots = slotsSnap.docs
        .map((s) => {
          const slotData = s.data() || {};

          return {
            id: s.id,
            ...slotData,
            startTime: toDateTime(slotData.startTime || slotData.startDateTime),
            endTime: toDateTime(slotData.endTime || slotData.endDateTime),

            // ✅ Always guarantee these fields exist
            assignedUsers: Array.isArray(slotData.assignedUsers)
              ? slotData.assignedUsers
              : [],

            assignedUserIds: Array.isArray(slotData.assignedUserIds)
              ? slotData.assignedUserIds
              : [],

            volunteersNeeded: Number(slotData.volunteersNeeded || 0),
          };
        })
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
        
      return activity;
    }));
    acts.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    setActivities(acts);
  };
  
  // ➕ Add new activity
  const handleAddActivity = async () => {
    logger.log("🛠️ Attempting to add activity...", {});
    logger.log("🔎 eventId:", {
      value: eventId
    });
    logger.log("📄 newActivity:", {
      value: newActivity
    });

    if (!eventId) {
      logger.warn("❌ Cannot add activity: eventId is null", {});
      showStatus(setStatus, { type: "error", message: "❌ Event not loaded yet. Please try again." });
      return;
    }

    if (!newActivity.title.trim()) {
      showStatus(setStatus, { type: "error", message: "❌ Title is required" });
      return;
    }
  
    try {
      logger.log("📤 Submitting to Firestore:", {
        value: `events/${eventId}/volunteers`
      });
      await addDoc(collection(db, "events", eventId, "volunteers"), {
        title: newActivity.title.trim(),
        description: newActivity.description.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
  
      // reset form
      setNewActivity({ title: "", description: "" });
      setShowAddActivityForm(false);
  
      // reload and sort alphabetically
      const snap = await getDocs(collection(db, "events", eventId, "volunteers"));
      const acts = await Promise.all(snap.docs.map(async (docSnap) => {
        const activity = { id: docSnap.id, ...docSnap.data() };
        const slotsSnap = await getDocs(collection(db, "events", eventId, "volunteers", docSnap.id, "timeSlots"));
        activity.timeSlots = slotsSnap.docs.map((s) => {
          const slotData = s.data() || {};

          return {
            id: s.id,
            ...slotData,
            startTime: toDateTime(slotData.startTime || slotData.startDateTime),
            endTime: toDateTime(slotData.endTime || slotData.endDateTime),
            assignedUsers: Array.isArray(slotData.assignedUsers)
              ? slotData.assignedUsers
              : [],
            assignedUserIds: Array.isArray(slotData.assignedUserIds)
              ? slotData.assignedUserIds
              : [],
            volunteersNeeded: Number(slotData.volunteersNeeded || 0),
          };
        });
        return activity;
      }));
  
      // sort by title
      acts.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
      setActivities(acts);
      await reloadActivities();
      showStatus(setStatus, { type: "success", message: "✅ Activity added" });
    } catch (err) {
      logger.error("Failed to add activity", {
        value: err
      });
      showStatus(setStatus, { type: "error", message: "❌ Failed to add activity" });
    }
  };
  
  // 🗑️ Delete activity
  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm("Delete this activity?")) return;
    await deleteDoc(doc(db, "events", eventId, "volunteers", activityId));
  };

  // ⏱️ Calculates end time from start time + shift length in minutes
  const calculateShiftEndTime = (startValue, durationMinutes) => {
    if (!startValue || !durationMinutes) return "";

    const startDate = new Date(startValue);

    if (Number.isNaN(startDate.getTime())) return "";

    startDate.setMinutes(startDate.getMinutes() + Number(durationMinutes));

    return startDate.toISOString();
  };

  const toDateTimeLocalValue = (date) => {
    const localDate = new Date(date);
    localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
    return localDate.toISOString().slice(0, 16);
  };

  // ➕ Add time slot
  const handleAddTimeSlot = async (activityId) => {
    const form = slotForms[activityId] || {};

    if (!form.start || !form.durationMinutes || !form.needed) {
      showStatus(setStatus, {
        type: "error",
        message: "❌ Start time, end time, and volunteers needed are required.",
      });
      return;
    }

    const startTime = new Date(form.start);
    const endTime = new Date(
      calculateShiftEndTime(form.start, form.durationMinutes)
    );
    if (endTime <= startTime) {
      showStatus(setStatus, { type: "error", message: "❌ End time must be after start time" });
      return;
    }

    try {
      await addDoc(collection(db, "events", eventId, "volunteers", activityId, "timeSlots"), {
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        volunteersNeeded: parseInt(form.needed),
        assignedUsers: [],
        assignedUserIds: [], // ✅ temporary legacy support
      });
      setSlotForms((prev) => ({
        ...prev,
        [activityId]: {
          ...form,
          start: toDateTimeLocalValue(endTime),
        },
      }));
      await reloadActivities(); // ✅ force fresh read    
      showStatus(setStatus, { type: "success", message: "✅ Time slot added" });
    } catch (err) {
      logger.error("❌ Failed to add time slot", {
        value: err
      });
      showStatus(setStatus, { type: "error", message: "❌ Failed to add time slot" });
    }
  };

  // 🗑️ Delete Volunteer Shift
  // ✅ Purpose:
  // - Allows managers/admins to remove an individual volunteer time slot
  // - Uses confirmation to prevent accidental deletion
  // - Reloads activities after delete so UI updates immediately
  const handleDeleteTimeSlot = async (activityId, slotId) => {
    const confirmDelete = window.confirm(
      "Delete this volunteer shift?\n\nThis will remove the time slot and any assigned volunteer records attached to this shift."
    );

    if (!confirmDelete) return;

    try {
      const result = await deleteVolunteerTimeSlot({ eventId, activityId, slotId });

      await reloadActivities();

      showStatus(setStatus, {
        type: "warning",
        message: result.data.failedEmailCount
          ? "Volunteer shift deleted. Some cancellation emails could not be sent."
          : "Volunteer shift deleted and volunteers notified.",
      });
    } catch (err) {
      logger.error("❌ Failed to delete volunteer shift:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to delete volunteer shift.",
      });
    }
  };

  const clearForm = (slotId) => {
    setVolunteerFormVisible((prev) => ({ ...prev, [slotId]: false }));
  };

  // 📥 Export
  // ✅ Purpose:
  // - Builds volunteer schedule rows
  // - Exports as a real XLSX Blob
  // - Fixes "Unrecognized type blob" by writing XLSX as an array buffer first
  const exportToXLSX = () => {
    try {
      const rows = [];

      activities.forEach((activity) => {
        (activity.timeSlots || []).forEach((slot) => {
          const assignedUsersCount = Array.isArray(slot.assignedUsers)
            ? slot.assignedUsers.length
            : 0;

          const assignedUserIdsCount = Array.isArray(slot.assignedUserIds)
            ? slot.assignedUserIds.length
            : 0;

          rows.push({
            Activity: activity.title || "",
            Start: slot.startTime || "",
            End: slot.endTime || "",
            Needed: Number(slot.volunteersNeeded || 0),
            Assigned: Math.max(assignedUsersCount, assignedUserIdsCount),
          });
        });
      });

      const sheet = XLSX.utils.json_to_sheet(rows);
      const book = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(book, sheet, "Schedule");

      const excelBuffer = XLSX.write(book, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      saveAs(blob, "volunteers.xlsx");

      showStatus(setStatus, {
        type: "success",
        message: "✅ Volunteer schedule exported successfully.",
      });
    } catch (err) {
      logger.error("❌ Failed to export volunteer schedule:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "❌ Failed to export volunteer schedule.",
      });
    }
  };

  // 📋 Totals
  // ✅ Purpose:
  // - Counts total volunteer shifts
  // - Counts unfilled volunteer spots, not just unfilled shift cards
  // - Supports both assignedUsers and assignedUserIds for backward compatibility
  const getTotals = () => {
    let totalSlots = 0;
    let unfilledSpots = 0;

    activities.forEach((activity) => {
      (activity.timeSlots || []).forEach((slot) => {
        totalSlots++;

        const assignedUsersCount = Array.isArray(slot.assignedUsers)
          ? slot.assignedUsers.length
          : 0;

        const assignedUserIdsCount = Array.isArray(slot.assignedUserIds)
          ? slot.assignedUserIds.length
          : 0;

        const assignedCount = Math.max(
          assignedUsersCount,
          assignedUserIdsCount
        );

        const neededCount = Number(slot.volunteersNeeded || 0);

        unfilledSpots += Math.max(neededCount - assignedCount, 0);
      });
    });

    return {
      total: totalSlots,
      unfilled: unfilledSpots,
    };
  };

  const { total, unfilled } = getTotals();

  if (!ready || eventLoading || !eventId) {
    return <FullPageSpinner message="Loading Volunteer..." />;
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1400px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">Volunteer Management</h1>
            <p className="lead text-muted mb-0">
              Create volunteer activities, manage time slots, assign volunteers, and export the schedule.
            </p>
          </div>
          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <PromotionSourceButton source="volunteers" sourceScope="collection" />
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
            role="alert"
          >
            {status.message}
          </div>
        )}

        {showAddActivityForm && (
          <div className="card shadow-sm mb-4 border-success">
            <div className="card-header bg-white">
              <strong>➕ New Volunteer Activity</strong>
              <div className="small text-muted">
                Create a volunteer area first, then add time slots underneath it.
              </div>
            </div>

            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-lg-5">
                  <label className="form-label fw-bold">
                    Activity Title*
                  </label>

                  <input
                    className="form-control"
                    value={newActivity.title}
                    onChange={(e) =>
                      setNewActivity((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    placeholder="Registration, Parking, Setup..."
                  />

                  <div className="form-text">
                    This is the main volunteer activity name shown to managers and volunteers.
                  </div>
                </div>

                <div className="col-12 col-lg-7">
                  <label className="form-label fw-bold">
                    Activity Description
                  </label>

                  <textarea
                    className="form-control"
                    rows={3}
                    value={newActivity.description}
                    onChange={(e) =>
                      setNewActivity((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Short description of what volunteers will help with..."
                  />

                  <div className="form-text">
                    Optional, but helpful for explaining the role.
                  </div>
                </div>

                <div className="col-12 d-flex flex-wrap gap-2">
                  <button
                    className="btn btn-success"
                    onClick={handleAddActivity}
                    disabled={!newActivity.title.trim()}
                  >
                    ✅ Save Volunteer Activity
                  </button>

                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setNewActivity({ title: "", description: "" });
                      setShowAddActivityForm(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
            <div>
              <strong>Volunteer Tools</strong>
              <div className="small text-muted">
                Manage volunteer activities, time slots, assignments, and schedule exports.
              </div>
            </div>

            <button
              className="btn btn-outline-secondary btn-sm align-self-start align-self-md-center"
              onClick={() => setExpandedAll((prev) => !prev)}
            >
              {expandedAll ? "Collapse All" : "Expand All"}
            </button>
          </div>

          <div className="card-body">
            <div className="row g-4">
              <div className="col-12 col-md-4">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small">Volunteer Activities</div>
                  <div className="fs-3 fw-bold">{activities.length}</div>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small">Total Time Slots</div>
                  <div className="fs-3 fw-bold">{total}</div>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small">Unfilled Slots</div>
                  <div className="fs-3 fw-bold">{unfilled}</div>
                </div>
              </div>
            </div>

            <div className="border-top mt-4 pt-3 d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => setShowAddActivityForm((prev) => !prev)}
              >
                {showAddActivityForm ? "Close Form" : "➕ Add Activity"}
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={exportToXLSX}
              >
                📤 Export
              </button>
            </div>
          </div>
        </div>

        {activities.length === 0 ? (
          <div className="card shadow-sm">
            <div className="card-body text-center py-5">
              <h4>No volunteer activities yet</h4>
              <p className="text-muted">
                Add your first volunteer activity to begin building the event schedule.
              </p>

              <button
                className="btn btn-primary"
                onClick={() => setShowAddActivityForm(true)}
              >
                ➕ Add Activity
              </button>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {activities.map((activity) => {
              const slots = activity.timeSlots || [];
              const assignedTotal = slots.reduce((sum, slot) => {
                const assignedUsersCount = Array.isArray(slot.assignedUsers)
                  ? slot.assignedUsers.length
                  : 0;

                const assignedUserIdsCount = Array.isArray(slot.assignedUserIds)
                  ? slot.assignedUserIds.length
                  : 0;

                return sum + Math.max(assignedUsersCount, assignedUserIdsCount);
              }, 0);
              const neededTotal = slots.reduce(
                (sum, slot) => sum + Number(slot.volunteersNeeded || 0),
                0
              );
              const openSpots = Math.max(neededTotal - assignedTotal, 0);

              return (
                <div key={activity.id} className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-white">
                      <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
                        <div>

                          {editingActivityId === activity.id ? (
                            <div className="border rounded bg-light p-3">
                              <div className="row g-3">
                                <div className="col-12 col-md-5">
                                  <label className="form-label fw-bold">
                                    Activity Title*
                                  </label>

                                  <input
                                    className="form-control"
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    placeholder="Volunteer activity title"
                                  />
                                </div>

                                <div className="col-12 col-md-5">
                                  <label className="form-label fw-bold">
                                    Activity Description
                                  </label>

                                  <input
                                    className="form-control"
                                    value={editDescription}
                                    onChange={(e) => setEditDescription(e.target.value)}
                                    placeholder="Optional description"
                                  />
                                </div>

                                <div className="col-12 col-md-2 d-flex gap-2">
                                  <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => handleSaveEdit(activity.id)}
                                  >
                                    Save
                                  </button>

                                  <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => setEditingActivityId(null)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <>
                              <h4 className="h5 fw-bold mb-1">
                                🙋 {activity.title || "Volunteer Activity"}
                              </h4>

                              {activity.description && (
                                <p className="text-muted mb-0">
                                  {activity.description}
                                </p>
                              )}
                            </>
                          )}
                        </div>

                        {editingActivityId !== activity.id && (
                          <div className="d-flex gap-2 flex-wrap align-self-start">
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => {
                                setEditingActivityId(activity.id);
                                setEditTitle(activity.title || "");
                                setEditDescription(activity.description || "");
                              }}
                            >
                              ✏️ Edit
                            </button>

                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDeleteActivity(activity.id)}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {expandedAll && (
                      <div className="card-body">
                        <div className="row g-4 mb-4">
                          <div className="col-12 col-md-4">
                            <div className="border rounded p-3 h-100">
                              <div className="text-muted small">Time Slots</div>
                              <div className="fs-4 fw-bold">{slots.length}</div>
                            </div>
                          </div>

                          <div className="col-12 col-md-4">
                            <div className="border rounded p-3 h-100">
                              <div className="text-muted small">Assigned</div>
                              <div className="fs-4 fw-bold">{assignedTotal}</div>
                            </div>
                          </div>

                          <div className="col-12 col-md-4">
                            <div className="border rounded p-3 h-100">
                              <div className="text-muted small">Open Spots</div>
                              <div className="fs-4 fw-bold">{openSpots}</div>
                            </div>
                          </div>
                        </div>

                        <div className="card border mb-4">
                          <div className="card-header bg-light">
                            <strong>Add Time Slot</strong>
                          </div>

                          <div className="card-body">
                            <div className="row g-3">
                              <div className="col-12 col-md-4">
                                <label className="form-label">Start Time</label>
                                <input
                                  type="datetime-local"
                                  className="form-control"
                                  value={slotForms[activity.id]?.start || ""}
                                  onChange={(e) =>
                                    setSlotForms((prev) => ({
                                      ...prev,
                                      [activity.id]: {
                                        ...(prev[activity.id] || {}),
                                        start: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              </div>

                              <div className="col-12 col-md-4">
                                <label className="form-label">Shift Length</label>
                                <select
                                  className="form-select"
                                  value={slotForms[activity.id]?.durationMinutes || ""}
                                  onChange={(e) =>
                                    setSlotForms((prev) => ({
                                      ...prev,
                                      [activity.id]: {
                                        ...(prev[activity.id] || {}),
                                        durationMinutes: e.target.value,
                                      },
                                    }))
                                  }
                                >
                                  <option value="">Select length</option>
                                  <option value="30">30 minutes</option>
                                  <option value="60">1 hour</option>
                                  <option value="90">1 hour 30 minutes</option>
                                  <option value="120">2 hours</option>
                                  <option value="150">2 hours 30 minutes</option>
                                  <option value="180">3 hours</option>
                                  <option value="210">3 hours 30 minutes</option>
                                  <option value="240">4 hours</option>
                                </select>

                                {slotForms[activity.id]?.start &&
                                  slotForms[activity.id]?.durationMinutes && (
                                    <div className="small text-muted mt-1">
                                      Ends at:{" "}
                                      {formatDateTime(
                                        calculateShiftEndTime(
                                          slotForms[activity.id]?.start,
                                          slotForms[activity.id]?.durationMinutes
                                        )
                                      )}
                                    </div>
                                  )}
                              </div>

                              <div className="col-12 col-md-2">
                                <label className="form-label">Needed</label>
                                <input
                                  type="number"
                                  min="1"
                                  className="form-control"
                                  value={slotForms[activity.id]?.needed || ""}
                                  onChange={(e) =>
                                    setSlotForms((prev) => ({
                                      ...prev,
                                      [activity.id]: {
                                        ...(prev[activity.id] || {}),
                                        needed: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              </div>

                              <div className="col-12 col-md-2 d-flex align-items-end">
                                <button
                                  className="btn btn-success w-100"
                                  onClick={() => handleAddTimeSlot(activity.id)}
                                >
                                  Add
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {slots.length === 0 ? (
                          <div className="alert alert-info mb-0">
                            No time slots have been added for this activity yet.
                          </div>
                        ) : (
                          <div className="row g-4">
                            {slots.filter(Boolean).map((slot) => {
                              const assignedUsersCount = Array.isArray(slot.assignedUsers)
                                ? slot.assignedUsers.length
                                : 0;

                              const assignedUserIdsCount = Array.isArray(slot.assignedUserIds)
                                ? slot.assignedUserIds.length
                                : 0;

                              const assignedCount = Math.max(
                                assignedUsersCount,
                                assignedUserIdsCount
                              );
                              const neededCount = Number(slot.volunteersNeeded || 0);
                              const slotOpenSpots = Math.max(
                                neededCount - assignedCount,
                                0
                              );

                              return (
                                <div
                                  key={slot.id}
                                  className="col-12 col-md-6 col-xl-4"
                                >
                                  <div className="card h-100 border shadow-sm">
                                    <div className="card-body">
                                      <div className="d-flex justify-content-between gap-3 mb-3">
                                        <div>
                                          <h5 className="mb-1">Volunteer Shift</h5>
                                          <div className="small text-muted">
                                            {formatDateTime(slot.startTime)} –{" "}
                                            {formatDateTime(slot.endTime)}
                                          </div>
                                        </div>

                                        <span
                                          className={`badge ${
                                            slotOpenSpots > 0
                                              ? "bg-warning text-dark"
                                              : "bg-success"
                                          } align-self-start`}
                                        >
                                          {slotOpenSpots > 0
                                            ? `${slotOpenSpots} open`
                                            : "Full"}
                                        </span>
                                      </div>

                                      <div className="d-flex flex-wrap gap-2 mb-3">
                                        <span className="badge bg-secondary">
                                          {assignedCount} assigned
                                        </span>

                                        <span className="badge bg-info text-dark">
                                          {neededCount} needed
                                        </span>
                                      </div>

                                      <AssignedVolunteersList
                                        slot={slot}
                                        activityId={activity.id}
                                        eventId={eventId}
                                        setStatus={setStatus}
                                      />

                                      <div className="d-flex flex-wrap gap-2 mt-3">
                                        <button
                                          className="btn btn-outline-primary btn-sm"
                                          onClick={() =>
                                            setVolunteerFormVisible((prev) => ({
                                              ...prev,
                                              [slot.id]: !prev[slot.id],
                                            }))
                                          }
                                        >
                                          {volunteerFormVisible[slot.id]
                                            ? "Close"
                                            : "➕ Assign Volunteer"}
                                        </button>

                                        <button
                                          className="btn btn-outline-danger btn-sm"
                                          onClick={() =>
                                            handleDeleteTimeSlot(activity.id, slot.id)
                                          }
                                        >
                                          🗑️ Delete Shift
                                        </button>
                                      </div>

                                      {volunteerFormVisible[slot.id] && (
                                        <div className="mt-3 border-top pt-3">
                                            <VolunteerAddForm
                                              eventId={eventId}
                                              activityId={activity.id}
                                              slotId={slot.id}
                                              setStatus={setStatus}
                                              assignedUsers={Array.isArray(slot?.assignedUsers) ? slot.assignedUsers : []}
                                              onAdded={async (volunteer) => {
                                                try {
                                                  logger.log("✅ VolunteerAddForm onAdded fired", {
                                                    value: volunteer,
                                                  });

                                                  // ✅ First update the UI so the assignment is visible immediately.
                                                  // Email sending should never block the volunteer workflow.
                                                  await reloadActivities();

                                                  clearForm(slot.id);

                                                  showStatus(setStatus, {
                                                    type: "success",
                                                    message: "✅ Volunteer assigned successfully.",
                                                  });

                                                  // 📧 Send email after the page is already updated.
                                                  // If email fails, we log it and show a warning, but do not undo the assignment.
                                                  if (volunteer?.email) {
                                                    try {
                                                      await sendConfirmationEmail(
                                                        volunteer.email,
                                                        activity.id,
                                                        slot.id
                                                      );

                                                      showStatus(setStatus, {
                                                        type: "warning",
                                                        message:
                                                          "Volunteer assigned and email sent. SMS notifications are not available yet.",
                                                      });
                                                    } catch (emailErr) {
                                                      logger.error("❌ Volunteer assigned, but invite email failed:", {
                                                        value: emailErr,
                                                      });

                                                      showStatus(setStatus, {
                                                        type: "warning",
                                                        message:
                                                          "Volunteer was assigned, but the invite email could not be sent.",
                                                      });
                                                    }
                                                  }
                                                } catch (err) {
                                                  logger.error("❌ Volunteer assigned, but UI refresh failed:", {
                                                    value: err,
                                                  });

                                                  showStatus(setStatus, {
                                                    type: "error",
                                                    message:
                                                      "Volunteer may have been assigned, but the page did not refresh correctly. Please refresh if needed.",
                                                  });
                                                }
                                              }}

                                            onCancel={() => clearForm(slot.id)}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );

};

export default Volunteers;
